import { useEffect, useRef, useState } from "react";
import type { CSSProperties, KeyboardEvent, PointerEvent, WheelEvent } from "react";
import { CENTRED, MAX_ZOOM, clampCrop, dragBy, ratioOf, sourceRect, writeCrop, zoomTo } from "@/lib/images";
import type { Crop } from "@/lib/images";
import { Slider } from "@/components/ui/slider";
import { cn } from "@/lib/utils";

/**
 * Framing a picture before it is sent (7g): the picture sits behind a frame
 * of its shape — a square for a profile picture, 16:9 for an event's banner —
 * and the person drags it into place and zooms, by finger, mouse, wheel,
 * slider or keyboard. What the frame holds travels in a hidden field, `name`,
 * which lib/images reads when the picture is drawn at the size it is sent.
 */
export function PictureFrame({
  file,
  aspect,
  name = "crop",
  round = false,
}: {
  file: File;
  aspect: string;
  name?: string;
  round?: boolean;
}) {
  const ratio = ratioOf(aspect) ?? 1;
  const [url, setUrl] = useState<string | null>(null);
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);
  const [crop, setCrop] = useState<Crop>(CENTRED);
  const [frameWidth, setFrameWidth] = useState(0);
  const frame = useRef<HTMLDivElement>(null);
  const zoomer = useRef<HTMLDivElement>(null);
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const pinch = useRef<{ distance: number; zoom: number } | null>(null);

  // A new picture starts in the middle, at the largest frame that fits.
  useEffect(() => {
    const next = URL.createObjectURL(file);
    setUrl(next);
    setSize(null);
    setCrop(CENTRED);
    return () => URL.revokeObjectURL(next);
  }, [file]);

  // The frame's width on screen, which a drag's pixels are measured against.
  useEffect(() => {
    const element = frame.current;
    if (!element) return;
    const measure = () => setFrameWidth(element.clientWidth);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const change = (next: (current: Crop) => Crop) => {
    if (!size) return;
    setCrop((current) => clampCrop(size.w, size.h, ratio, next(current)));
  };

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    event.currentTarget.setPointerCapture?.(event.pointerId);
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      pinch.current = { distance: Math.hypot(a!.x - b!.x, a!.y - b!.y), zoom: crop.zoom };
    }
  };
  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const last = pointers.current.get(event.pointerId);
    if (!last || !size) return;
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (pointers.current.size === 2 && pinch.current) {
      const [a, b] = [...pointers.current.values()];
      const distance = Math.hypot(a!.x - b!.x, a!.y - b!.y);
      const zoom = pinch.current.zoom * (distance / Math.max(1, pinch.current.distance));
      change((current) => zoomTo(size.w, size.h, ratio, current, zoom));
      return;
    }
    const dx = event.clientX - last.x;
    const dy = event.clientY - last.y;
    change((current) => dragBy(size.w, size.h, ratio, current, dx, dy, frameWidth || 1));
  };
  const onPointerUp = (event: PointerEvent<HTMLDivElement>) => {
    pointers.current.delete(event.pointerId);
    if (pointers.current.size < 2) pinch.current = null;
  };
  const onWheel = (event: WheelEvent<HTMLDivElement>) => {
    if (!size) return;
    const factor = event.deltaY < 0 ? 1.1 : 1 / 1.1;
    change((current) => zoomTo(size.w, size.h, ratio, current, current.zoom * factor));
  };
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!size) return;
    const step = 10;
    const moves: Record<string, [number, number]> = {
      ArrowLeft: [step, 0],
      ArrowRight: [-step, 0],
      ArrowUp: [0, step],
      ArrowDown: [0, -step],
    };
    const move = moves[event.key];
    if (move) {
      event.preventDefault();
      change((current) => dragBy(size.w, size.h, ratio, current, move[0], move[1], frameWidth || 1));
    } else if (event.key === "+" || event.key === "=") {
      change((current) => zoomTo(size.w, size.h, ratio, current, current.zoom * 1.1));
    } else if (event.key === "-") {
      change((current) => zoomTo(size.w, size.h, ratio, current, current.zoom / 1.1));
    }
  };

  // shadcn's slider names its thumb — the part a screen reader hears as the
  // slider — only from a prop it does not pass on, so its name is set here.
  useEffect(() => {
    zoomer.current?.querySelector('[role="slider"]')?.setAttribute("aria-label", "Zoom");
  });

  // Where the picture sits behind the frame: scaled so the crop fills it, and
  // moved so the crop's centre is the frame's.
  let placement: CSSProperties = { visibility: "hidden" };
  if (size && frameWidth > 0) {
    const rect = sourceRect(size.w, size.h, ratio, crop);
    const scale = frameWidth / rect.sw;
    placement = {
      width: size.w * scale,
      height: size.h * scale,
      left: -rect.sx * scale,
      top: -rect.sy * scale,
    };
  }

  return (
    <div className="space-y-2">
      <div
        ref={frame}
        role="group"
        tabIndex={0}
        aria-label="Drag the picture to frame it; use the slider or the + and − keys to zoom"
        data-testid="picture-frame"
        className={cn(
          "relative w-full cursor-grab touch-none select-none overflow-hidden bg-muted outline-none focus-visible:ring-3 focus-visible:ring-ring/50 active:cursor-grabbing",
          round ? "rounded-full" : "rounded-2xl",
        )}
        style={{ aspectRatio: String(ratio) }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onWheel={onWheel}
        onKeyDown={onKeyDown}
      >
        {url && (
          <img
            src={url}
            alt=""
            draggable={false}
            className="pointer-events-none absolute max-w-none"
            style={placement}
            onLoad={(event) => setSize({ w: event.currentTarget.naturalWidth, h: event.currentTarget.naturalHeight })}
          />
        )}
      </div>
      <div ref={zoomer} className="flex items-center gap-3 text-sm text-muted-foreground">
        <span aria-hidden="true">Zoom</span>
        <Slider
          min={1}
          max={MAX_ZOOM}
          step={0.01}
          value={[crop.zoom]}
          disabled={!size}
          onValueChange={([zoom]) => {
            if (zoom === undefined) return;
            change((current) => (size ? zoomTo(size.w, size.h, ratio, current, zoom) : current));
          }}
          className="flex-1 py-2"
        />
      </div>
      <input type="hidden" name={name} value={writeCrop(crop)} />
    </div>
  );
}
